import { createContext, useState, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { groupBy } from 'lodash';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import { useDIContext } from '@/Framework/DI/DIContext';
import SubscriptionsRepository from '@/finsight/infrastructure/repository/SubscriptionsRepository';
import {
  getSubscriptionsArticleParams,
  IArticleParamsConfig, getSubscribeParams, getPageIsOpened, getFilteredThemes,
} from '@/finsight/application/CreditFlowResearch/ArticlesSubscriptions/helpers';
import { usePageFiltersContext } from '@/finsight/application/PageFilters/PageFiltersContext';
import { ISubscriptionsPayloadTheme } from '@/finsight/domain/vo/Subscriptions';
import { ARTICLE_SUBSCRIPTION_ID } from '@/finsight/ui/components/SubscriptionForm/const';
import {
  useUserSubscriptionsContext,
} from '@/finsight/application/Subscriptions/UserSubscriptionsContext/UserSubscriptionsContext';
import {
  getGroupedThemesIds,
} from '@/finsight/ui/components/creditFlow/articles/posts/PageSubscriptionButton/helpers';
import { ThemeType } from '@/finsight/domain/vo/CreditFlow/Articles/themeTypes';
import { useArticlesThemesContext } from '@/finsight/application/CreditFlowResearch/ArticlesMarketThemesContext';
import { useDictionariesContext } from '@/finsight/application/Dictionaries/DictionariesContext';

const useArticlesSubscriptions = (themeType: ThemeType) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { container } = useDIContext();
  const { pathname, asPath, query } = useRouter();
  const { companyViewId, sector: querySector, industry: queryIndustry } = query;
  const subscriptionsRepository = container.get(SubscriptionsRepository);
  const { sectorsList, industriesList } = useDictionariesContext();
  const { userSubscriptions, getUserSubscriptions, setCommonButtonSubscribed } = useUserSubscriptionsContext();
  const {
    articleParams: subscriptionArticleParams,
    subscriptionIds,
  } = getSubscriptionsArticleParams(userSubscriptions);
  const { themes, isFetching: isThemesFetching } = useArticlesThemesContext();

  const {
    pageFilters: {
      productIds,
      regionIds,
      industry,
      sector,
      includedParentId,
      includedCompanyRole,
      isConsumerOverview,
      profileType,
    },
    regionFilters,
    productFilters,
  } = usePageFiltersContext();
  const currentRegion = regionFilters.find(({ id }) => id === regionIds[0]) || {};
  const currentProduct = productFilters.find(({ id }) => id === productIds[0]);

  const isOneFilterSelected = productIds.length === 1 && regionIds.length === 1;
  const isCommonButtonUsed = !!companyViewId || !!querySector || !!queryIndustry;
  const activePage = getPageIsOpened(query);

  const paramsConfig: IArticleParamsConfig = {
    themes,
    regionIds,
    productIds,
    isConsumerOverview,
    themeType,
    sector,
    industry,
    sectorsList,
    industriesList,
    includedParentId,
    includedCompanyRole,
    pathname,
    profileType,
  };

  const onSubscribe = async (articleFormThemes: ISubscriptionsPayloadTheme[] = []) => {
    const articleParams = getSubscribeParams({
      subscriptionArticleParams,
      paramsConfig,
      articleFormThemes,
      pageType: activePage,
    });

    const payload = {
      subscriptionIds: [...subscriptionIds, ARTICLE_SUBSCRIPTION_ID],
      articleParams,
    };

    try {
      await subscriptionsRepository.updateUserSubscriptionsBySSID(payload);
      isCommonButtonUsed ? setCommonButtonSubscribed(true) : setIsSubscribed(true);
      getUserSubscriptions(); // if user wants to subscribe again, payload should have existing subscriptions
      AlertManager.success('Your subscription has been successfully updated');
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    }
  };

  const getPageSubscriptionStatus = async () => {
    setIsFetching(true);

    const articleParams = getSubscribeParams({
      paramsConfig,
      pageType: activePage,
    });

    const payload = {
      subscriptionId: ARTICLE_SUBSCRIPTION_ID,
      articleParams,
    };
    try {
      const response: boolean = await subscriptionsRepository.checkUserSubscriptionStatus(payload);
      isCommonButtonUsed ? setCommonButtonSubscribed(response) : setIsSubscribed(response);
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    } finally {
      setIsFetching(false);
    }
  };

  const { groupedThemes, themesIds } = useMemo(() => {
    const filteredThemes = getFilteredThemes(
      themes,
      currentRegion,
      currentProduct,
    );
    const groupedThemes = groupBy(filteredThemes, 'type');

    return {
      groupedThemes,
      themesIds: getGroupedThemesIds(groupedThemes),
    };
  },
  [themes, currentRegion, currentProduct]);

  const accentToCheckSubscription = (companyViewId || isOneFilterSelected) && themeType && themes.length;

  useEffect(() => {
    if (accentToCheckSubscription) {
      getPageSubscriptionStatus();
    }
  }, [accentToCheckSubscription, asPath]);

  return {
    isSubscribed,
    onSubscribe,
    getPageSubscriptionStatus,
    isOneFilterSelected,
    isFetching,
    groupedThemes,
    themesIds,
    themes,
    isThemesFetching,
    currentRegion,
    currentProduct,
    isCommonButtonUsed,
  };
};

type ArticlesSubscriptionsContextType = ReturnType<typeof useArticlesSubscriptions>;

export const ArticlesSubscriptionsContext = createContext<ArticlesSubscriptionsContextType>(null);

export const useArticlesSubscriptionsContext = () => {
  const context = useContext(ArticlesSubscriptionsContext);
  if (!context) {
    throw new Error('useArticlesSubscriptionsContext must be used within the ArticlesSubscriptionsContext');
  }
  return context;
};

interface IContextProps {
  children: ReactNode,
  themeType?: ThemeType,
}

const ArticleSubscriptionsContextProvider = ({ children, themeType }: IContextProps) => (
  <ArticlesSubscriptionsContext.Provider value={ useArticlesSubscriptions(themeType) }>
    { children }
  </ArticlesSubscriptionsContext.Provider>
);

export default ArticleSubscriptionsContextProvider;
